import { PartRecord } from "internal";
import PartsList from "organisms/parts-list/parts-list";
import React from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useAdminPreview from "utilities/hooks/use-admin-preview";
import StringUtils from "utilities/string-utils";
import SectionTableOfContentsRecord from "models/view-models/table-of-contents/section-table-of-contents-record";
import { SectionParams } from "interfaces/routing/route-params";
import { useParams } from "react-router-dom";
import { SectionParentType } from "utilities/enumerations/section-parent-type";
import useSectionScrollSpyHandlers from "utilities/hooks/scroll-spy/use-section-scroll-spy-handlers";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import TableOfContentsCollapsePanelLinkWithinPanel from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/table-of-contents-collapse-panel-link-within-panel";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface VirtualTableOfContentsSectionProps {
    chapterNfpaLabel?: string;
    parts?: Array<PartRecord>;
    sections: SectionTableOfContentsRecord[];
    /**
     * Flag to drive whether the component uses 'hash' links (such as #ID000700000036) to link to
     * the SectionDetail element on the page, or a direct link to the SectionPage route
     *
     * @default true
     */
    useHashLinks?: boolean;
    parentType: SectionParentType;
}

const CSS_CLASS_NAME = "c-virtual-table-of-contents";
const PARAGRAPH_CLASS_NAME = `${CSS_CLASS_NAME} -part`;
// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const VirtualTableOfContentsSection: React.FunctionComponent<
    VirtualTableOfContentsSectionProps
> = (props: VirtualTableOfContentsSectionProps) => {
    const { parts, sections, chapterNfpaLabel, parentType } = props;
    const { useHashLinks } = props ?? true;
    const { isAdminPreview } = useAdminPreview();
    const sectionParams = useParams<SectionParams>();

    // Allows for expandable ToC and non-expandable ToC views to function
    const params = {
        ...sectionParams,
        nfpaLabel: chapterNfpaLabel ?? sectionParams.chapterNfpaLabel,
    };

    const { handleClick: sectionScrollSpy } = useSectionScrollSpyHandlers();

    const handleClick = (id) => {
        sectionScrollSpy(id);
    };

    // If we have no Parts to group the Sections by, return the 'simple' or ungrouped table of contents
    if (CollectionUtils.isEmpty(parts)) {
        return (
            <nav className={`${CSS_CLASS_NAME} -section`}>
                <ul className={`${CSS_CLASS_NAME}__list`}>
                    {renderSectionList(sections, handleClick, useHashLinks)}
                </ul>
            </nav>
        );
    }

    const sectionsWithoutPartId = sections.filter((s) => s.partId == null);
    const renderSections = (partId?: number) =>
        renderSectionListWithHashLinks(sections, handleClick, partId);

    return (
        <nav className={`${CSS_CLASS_NAME} -section`}>
            {CollectionUtils.hasValues(sectionsWithoutPartId) && (
                <ul>
                    {renderSectionList(
                        sectionsWithoutPartId,
                        handleClick,
                        useHashLinks
                    )}
                </ul>
            )}
            <PartsList
                paragraphClassName={PARAGRAPH_CLASS_NAME}
                parts={parts}
                renderSections={renderSections}
                sections={sections}
                ulClassName={`${CSS_CLASS_NAME}__list`}
            />
        </nav>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

const renderSectionListWithHashLinks = (
    sections: Array<SectionTableOfContentsRecord>,
    handleClick: (id: number) => void,
    partId?: number
): JSX.Element[] => {
    const filteredSections = sections.filter((s) => s.partId === partId);

    return renderSectionList(filteredSections, handleClick, true);
};

const renderSectionList = (
    sections: Array<SectionTableOfContentsRecord>,
    handleClick: (id: number) => void,
    isPublic: boolean = false
): JSX.Element[] => {
    return sections
        .filter(
            (x) => !x.isDeleted && StringUtils.hasValue(x.getDisplayTitle())
        )
        .sort((a, b) => (a.displaySequence ?? 0) - (b.displaySequence ?? 0))
        .map((section: SectionTableOfContentsRecord) => {
            return (
                <TableOfContentsCollapsePanelLinkWithinPanel
                    key={section.id}
                    dataTestDisplaySequence={section.displaySequence}
                    dataTestExternalId={section.externalId}
                    dataTestId={"section-link"}
                    hasBookmark={section.hasBookmark}
                    index={section.id}
                    isPublic={isPublic}
                    onClick={() => handleClick(section.id ?? 0)}
                    useRelativePath={true}
                    caapsId={section.id}
                    publicationComponentType={PublicationComponentType.Section}
                    parentId={section.chapterId?.toString()}>
                    <span>{section.getDisplayTitle()}</span>
                </TableOfContentsCollapsePanelLinkWithinPanel>
            );
        });
};

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

export default VirtualTableOfContentsSection;

// #endregion Export
